/*
 * DeliveryCard — iter101.98 (compact strip)
 *
 * Was previously a 3-cell card (Planning type · Priority · Target
 * completion). We killed the first two because they were redundant:
 *   - Priority is already surfaced by the case-header status pill
 *   - Planning type barely matters post-planner-assignment
 *
 * What remains is a single compact horizontal strip carrying only the
 * "target-date contract" — the one thing admin actually manages here.
 * The strip is a plain flex row: icon + eyebrow + date input + optional
 * "Reset to SLA" button + right-aligned CountdownRibbon. An optional
 * hint line renders below when the target has been manually overridden.
 */
import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Package, Calendar, Clock, Warning } from "@phosphor-icons/react";


/* States where the delivery countdown is no longer meaningful — the
   case has left the factory or is otherwise off the manufacturing
   clock. Hiding the ribbon in these states prevents stale "overdue"
   noise on cases that already shipped or wrapped. */
const COUNTDOWN_IRRELEVANT_STATUSES = new Set([
    "shipped", "aligners_received", "treatment_started", "in_treatment",
    "refinement_requested", "completed", "cancelled", "on_hold",
]);


/**
 * CountdownRibbon — small right-aligned pill showing the live delta to
 * `target_completion_date`.
 *
 * Tone thresholds (iter101.97):
 *   - > 48h remaining         → subtle primary teal · Clock icon · no motion
 *   - 24h - 48h remaining     → amber · Warning icon · pulsing
 *   - < 24h remaining         → amber · Warning icon · pulsing (hours if ≤6h)
 *   - overdue                 → rose · Warning icon · pulsing
 *
 * Hidden when: no target date is set, or the case is past the point
 * where the delivery deadline still matters (see
 * `COUNTDOWN_IRRELEVANT_STATUSES`).
 */
function CountdownRibbon({ targetDate, caseStatus }) {
    if (!targetDate) return null;
    if (COUNTDOWN_IRRELEVANT_STATUSES.has(caseStatus)) return null;

    /* Anchor the target at end-of-day so a case dated "2026-08-20"
       stays green throughout the 20th and doesn't tip to "overdue"
       at midnight local. */
    const target = new Date(String(targetDate).slice(0, 10) + "T23:59:59");
    if (isNaN(target.getTime())) return null;

    const now = new Date();
    const deltaMs = target.getTime() - now.getTime();
    const deltaHours = deltaMs / 3600000;
    const isOverdue = deltaMs < 0;
    const isInside48h = !isOverdue && deltaHours < 48;

    let tone, Icon, label, pulse;
    if (isOverdue) {
        tone = "rose";
        Icon = Warning;
        pulse = true;
        const overdueDays = Math.floor(Math.abs(deltaHours) / 24);
        if (overdueDays >= 1) {
            label = `${overdueDays} day${overdueDays === 1 ? "" : "s"} overdue`;
        } else {
            label = "Overdue today";
        }
    } else if (isInside48h) {
        tone = "amber";
        Icon = Warning;
        pulse = true;
        if (deltaHours < 24) {
            const startOfTargetDay = new Date(String(targetDate).slice(0, 10) + "T00:00:00");
            const isToday = now >= startOfTargetDay;
            if (isToday) {
                const hoursLeft = Math.max(1, Math.floor(deltaHours));
                label = hoursLeft <= 6 ? `${hoursLeft}h to target` : "Due today";
            } else {
                label = "Due tomorrow";
            }
        } else {
            label = "Due tomorrow";
        }
    } else {
        tone = "primary";
        Icon = Clock;
        pulse = false;
        const daysLeft = Math.ceil(deltaHours / 24);
        if (daysLeft <= 7) {
            label = `${daysLeft} day${daysLeft === 1 ? "" : "s"} to target`;
        } else if (daysLeft <= 14) {
            label = "~1 week to target";
        } else if (daysLeft <= 21) {
            label = "~2 weeks to target";
        } else {
            label = `${daysLeft} days to target`;
        }
    }

    const toneClasses = {
        rose:    "bg-rose-50 text-rose-700 border-rose-200",
        amber:   "bg-amber-50 text-amber-800 border-amber-200",
        primary: "bg-primary/8 text-primary border-primary/20",
    }[tone];

    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10.5px] font-bold uppercase tracking-[0.08em] ${toneClasses} ${pulse ? "animate-pulse" : ""}`}
            data-testid={`delivery-countdown-${tone}`}
            title={`Target ${String(targetDate).slice(0, 10)}`}
        >
            <Icon size={11} weight="fill" />
            {label}
        </span>
    );
}


/* ── Main export ───────────────────────────────────────────────────── */
/**
 * Compact one-strip Delivery card. Renders:
 *   [icon] Target Completion  [date input]  [Reset to SLA?]  [Countdown]
 *   (optional)  hint line — "Auto · Rush SLA · 5d" or "Manually overridden · SLA said 15 Aug"
 *
 * The `ops-target-date`, `ops-target-reset-btn` and `delivery-card`
 * test-ids are preserved so downstream flows + regressions stay green.
 */
export function DeliveryCard({ c, reload }) {
    const source = c?.target_completion_source;
    const slaDays = c?.target_completion_sla_days ?? c?.pricing?.priority_tat_days;
    const priorityLabel = c?.pricing?.priority_label || "Priority";
    const currentDate = (c?.target_completion_date || "").slice(0, 10);

    /* Recompute the SLA-derived date so we can offer "Reset to SLA"
       and surface the delta when admin overrode the auto value. */
    const slaDate = React.useMemo(() => {
        if (!slaDays) return null;
        let anchor = null;
        for (const row of c?.timeline || []) {
            if (row.action === "doctor_approved") {
                anchor = new Date(row.at || row.created_at || row.timestamp || 0);
                break;
            }
        }
        if (!anchor || isNaN(anchor.getTime())) {
            anchor = c?.status_updated_at ? new Date(c.status_updated_at) : new Date();
        }
        const target = new Date(anchor.getTime() + Number(slaDays) * 86400000);
        return target.toISOString().slice(0, 10);
    }, [c?.timeline, c?.status_updated_at, slaDays]);

    const fmtDate = (iso) => {
        if (!iso) return "";
        try {
            const d = new Date(iso + "T00:00:00");
            return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        } catch { return iso; }
    };

    const setTarget = async (dateStr) => {
        try {
            await api.put(`/admin/cases/${c.id}/target-date`, { target_completion_date: dateStr });
            reload?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't update target date");
        }
    };
    const resetTargetToSla = async () => {
        try {
            const { data } = await api.post(`/admin/cases/${c.id}/target-date/reset`);
            toast.success(`Reset to SLA · target ${data.target_completion_date}`);
            reload?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't reset target date");
        }
    };

    /* Hint line — only when we have something to say. */
    let hint = null;
    if (source === "auto" && slaDays) {
        hint = `Auto · ${priorityLabel} SLA · ${slaDays} day${slaDays === 1 ? "" : "s"} from approval.`;
    } else if (source === "manual" && slaDate && slaDate !== currentDate) {
        hint = `Manually overridden · SLA said ${fmtDate(slaDate)}.`;
    } else if (source === "manual") {
        hint = "Manually set.";
    }

    const showResetBtn = source === "manual" && slaDate && slaDate !== currentDate;

    return (
        <div
            className="rounded-2xl border border-border bg-card px-5 py-3"
            data-testid="delivery-card"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <div className="flex items-center gap-1.5 shrink-0">
                        <Package size={13} weight="fill" className="text-primary" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground">
                            Target Completion
                        </span>
                    </div>
                    <input
                        type="date"
                        value={currentDate}
                        onChange={(e) => setTarget(e.target.value)}
                        className="h-8 text-[13px] rounded-md border border-border bg-background px-2 font-mono"
                        data-testid="ops-target-date"
                    />
                    {showResetBtn && (
                        <button
                            type="button"
                            onClick={resetTargetToSla}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition"
                            data-testid="ops-target-reset-btn"
                        >
                            <Calendar size={10} weight="fill" /> Reset to SLA
                        </button>
                    )}
                </div>
                <CountdownRibbon
                    targetDate={c?.target_completion_date}
                    caseStatus={c?.status}
                />
            </div>
            {hint && (
                <div className="mt-1.5 text-[10.5px] text-muted-foreground leading-snug">
                    {hint}
                </div>
            )}
        </div>
    );
}
